import { useOnce } from '@mentimeter/react-hooks';
import { useEffect } from 'react';
import type { OpenEndedStaticContent } from '@core-api/presentation/types/response';
import { useFetchTextResponses } from '@mentimeter/presentation-data-hooks/fetch-text-responses';
import { trackUser } from '@api/tracking/client';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../legacy-context/presentationContext';
import { getVisualizationState } from '../../utils/getVisualizationState';
import { useScreenshotReady } from '../../shared/hooks/use-screenshot-ready';
import { useOpenPreviewResults } from './hooks/use-open-preview-results';
import { useOpenSlideState } from './presentation/utils/useOpenSlideState';
import { PresentationModule } from './PresentationModule';
import { OpenEndedPresentationContext } from './OpenEndedPresentationContext';
import { getNumberOfColumns } from './utils/getNumberOfColumns';

export function OpenPresentationDataHandler() {
  const {
    useConfig,
    usePace,
    isPreviewResultsEnabled,
    useSeries,
    useCurrentSession,
  } = usePresentationContext();
  const { mode, enableAnimations, readonly } = useConfig();
  const { currentStep } = usePace();

  const {
    slideAdminKey,
    images,
    title,
    staticContent,
    interactiveContents,
    responseVisibility,
  } = usePresentationSlideContext();

  const interactiveContent = interactiveContents[0]!;

  const [{ shouldShowGroups }, setSlideState] = useOpenSlideState();
  const { isCurrentSessionActive, activeSessionId } = useCurrentSession();

  const { participation_identity_mode, participation_identity_visibility } =
    useSeries();
  const hasIdentifiedResultsSettingOnAndVisible =
    participation_identity_mode === 'identified' &&
    participation_identity_visibility === 'everyone';

  const shouldFetchIdentifiedResponses =
    hasIdentifiedResultsSettingOnAndVisible && isCurrentSessionActive;

  const { data, isLoading: isLoadingTextResponses } = useFetchTextResponses({
    adminKey: slideAdminKey,
    interactiveContentId: interactiveContent.interactiveContentId,
    sessionId: isCurrentSessionActive ? undefined : activeSessionId,
  });

  const responses = data?.responses ?? [];

  const previewResults = useOpenPreviewResults({
    isEnabled: isPreviewResultsEnabled,
  });

  const visibleResponses = isPreviewResultsEnabled ? previewResults : responses;

  const visualizationState = getVisualizationState({
    votingOnAnswers:
      interactiveContent.voteSettings?.voteOnResponsePolicy === 'allow-votes',
    mode,
    numberOfAnswers: visibleResponses.length,
    currentStep,
  });

  const shouldHideResults =
    responseVisibility === 'hidden' || visualizationState === 'hide-answers';

  function trackViewedSlideState(
    state: 'start-voting' | 'viewed-upvoting-results',
  ) {
    trackUser({
      event: 'Viewed slide state',
      properties: {
        questionType: 'open',
        state,
        context: shouldShowGroups
          ? 'grouped-responses'
          : 'individual-responses',
      },
    });
  }

  useOnce(visualizationState === 'start-voting', () =>
    trackViewedSlideState('start-voting'),
  );
  useOnce(visualizationState === 'view-results', () =>
    trackViewedSlideState('viewed-upvoting-results'),
  );

  useEffect(() => {
    if (shouldShowGroups) {
      // reset the state when pacing through visualization states
      setSlideState({
        activeClusterId: null,
        activeResponseIndex: null,
      });
    }
  }, [visualizationState, setSlideState, shouldShowGroups]);

  useScreenshotReady({
    isReady: mode === 'screenshot' && !isLoadingTextResponses,
  });

  if (shouldFetchIdentifiedResponses && isLoadingTextResponses) {
    return null;
  }

  const openEndedStaticContent = staticContent as OpenEndedStaticContent;

  const numberOfColumns = getNumberOfColumns({
    hasImages: images.length > 0,
    snapDirection: openEndedStaticContent.layout?.snapDirection ?? 'x',
    imageLayoutPosition: openEndedStaticContent.image?.imageLayoutPosition ?? {
      start: 0,
      end: 0,
    },
    gridColumns: openEndedStaticContent.layout?.gridColumns ?? 0,
  });

  return (
    <OpenEndedPresentationContext.Provider
      value={{
        title,
        styledTitle: openEndedStaticContent.styledTitle as Record<
          string,
          unknown
        >,
        label: openEndedStaticContent.label,
        description: interactiveContent.description,
        textSizeOffset: openEndedStaticContent.textSizeOffset,
        shouldAnimate: enableAnimations,
        isReadonly: readonly,
        mode,
        visualization:
          openEndedStaticContent.interactiveContentStyling?.visualization ??
          'speech_bubbles',
      }}
    >
      <PresentationModule
        visualizationState={visualizationState}
        responses={visibleResponses}
        numberOfVoters={data?.upvoterCount || 0}
        isUpvotingEnabled={
          interactiveContent.voteSettings?.voteOnResponsePolicy ===
          'allow-votes'
        }
        shouldHideResults={shouldHideResults}
        mode={mode}
        numberOfColumns={numberOfColumns}
        shouldShowGroups={!!shouldShowGroups}
      />
    </OpenEndedPresentationContext.Provider>
  );
}
